/**
 * Pure helpers for html_css_workspace section link navigation.
 *
 * An HTML/CSS section renders inside a sandboxed iframe with no
 * allow-same-origin and (in inline mode) no allow-top-navigation, so a plain
 * `<a href="/about">` inside admin-authored markup cannot navigate the app.
 * Page Builder therefore lets the admin map any link/button in that markup to a
 * page — `section_config.link_navigations` — and this module builds the
 * in-iframe click bridge plus resolves each mapping to an href.
 *
 * The iframe only ever posts back a nav id. The parent resolves the actual
 * destination from its own copy of the config, so section JS cannot talk the
 * app into navigating anywhere the admin did not configure.
 *
 * No React/network dependencies — see HtmlCssSection.js for the component that
 * wires these in, and arcee-admin's htmlLinkTargets.js for the authoring side.
 */

import { pageHref } from './pageHref';

export const NAV_MESSAGE_TYPE = 'ARCEE_NAV';

/**
 * Resolves one link_navigations entry to an href.
 * Returns null when the entry has no usable destination — an unconfigured
 * detected element, or a page mapping whose page was deleted.
 */
export function resolveNavHref(nav) {
  if (!nav) return null;

  if (nav.navType === 'page') {
    const slug = String(nav.targetPageSlug || '').trim().replace(/^\/+/, '');
    if (!slug) return null;
    const pairs = (nav.queryParams || [])
      .filter((p) => p && p.key)
      .map((p) => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value ?? '')}`);
    const href = pageHref(slug);
    return pairs.length ? `${href}?${pairs.join('&')}` : href;
  }

  if (nav.navType === 'url') {
    const url = String(nav.url || '').trim();
    return url || null;
  }

  return null;
}

/** True for hrefs that react-router can handle in-app rather than a full page load. */
export function isInternalHref(href) {
  return typeof href === 'string' && href.startsWith('/') && !href.startsWith('//');
}

// One source of truth for the placeholder, so "does this need a record?" and
// "fill the record in" cannot disagree about whether `{ id }` counts. Kept
// non-global for the test — a global regex carries lastIndex between calls and
// would answer differently on alternate invocations.
const RECORD_PLACEHOLDER = /\{\s*id\s*\}/;

/** True when a configured destination is per-record rather than one fixed page. */
export function isRecordHref(href) {
  return typeof href === 'string' && RECORD_PLACEHOLDER.test(href);
}

/**
 * Fills the record id into a configured destination like `/p/event/{id}`.
 *
 * THE SPLIT THAT KEEPS THIS SAFE: the template comes from the section's stored
 * configuration and the id comes from the clicked card. So markup rendered from
 * bound data can choose WHICH record opens, but never WHERE the app goes — the
 * same division that keeps a destination in configuration rather than in
 * the markup a visitor can reach.
 *
 * The id is URL-encoded, so a value containing `/` or `?` cannot invent path
 * segments or query parameters. A missing id, or a template that resolves to
 * anything other than a single-slash relative path, yields null — no
 * navigation at all beats navigating somewhere wrong.
 */
export function fillRecordHref(template, recordId) {
  if (!isRecordHref(template)) return null;
  if (recordId === null || recordId === undefined || recordId === '') return null;

  const href = template.replace(new RegExp(RECORD_PLACEHOLDER.source, 'g'),
                                encodeURIComponent(String(recordId)));
  if (!isInternalHref(href)) return null;
  return href;
}

/** Drops entries that resolve to nothing and pairs each survivor with its href. */
export function usableNavs(navigations) {
  return (navigations || [])
    .filter((nav) => nav && nav.id && nav.selector)
    .map((nav) => ({ nav, href: resolveNavHref(nav) }))
    .filter(({ href }) => href !== null);
}

/**
 * Builds the script injected into the section iframe that turns configured
 * elements into app navigations.
 *
 * Clicks are handled by ONE delegated listener on the document, matched against
 * the configured selectors at click time, rather than a listener per element.
 * Section JS redraws its markup whenever it likes — a search or a new page via
 * arceeQuery replaces every card — and a per-element listener bound at load
 * never reaches the redrawn ones, so "View event" went dead after a search.
 * Hrefs and the pointer are still stamped on each element (inline, on load,
 * and again whenever the DOM changes); `data-arcee-nav` marks elements already
 * stamped so the repeats skip them.
 *
 * Three details of the emitted script, explained here rather than inside it —
 * every byte in that template is shipped into each iframe:
 *
 * - the record id is read from the element or its nearest ancestor, so the
 *   attribute can sit on the card while the selector targets a link inside it;
 * - anchors get a real href so hover previews and the pointer match the
 *   destination, though the click is still intercepted since the sandbox would
 *   block the navigation itself;
 * - a per-record destination with no id is left ALONE rather than given the raw
 *   template — the click is inert either way, and "/p/event/{id}" in the status
 *   bar advertises a page that does not exist.
 *
 * Returns '' when nothing is configured, so sections that don't use the feature
 * carry no extra script at all.
 */
export function buildLinkNavScript(navigations, instanceId) {
  const entries = usableNavs(navigations).map(({ nav, href }) => ({
    id: nav.id,
    selector: nav.selector,
    href,
  }));
  if (entries.length === 0) return '';

  // `<` is escaped so the payload can never close the surrounding <script>.
  const json = JSON.stringify(entries).replace(/</g, '\\u003c');

  return `;(function(){
  var NAVS = ${json};
  var IFRAME_ID = '${instanceId}';
  var RECORD = /\\{\\s*id\\s*\\}/;
  function recordIdOf(el) {
    var holder = el.closest ? el.closest('[data-arcee-record-id]') : null;
    return holder ? holder.getAttribute('data-arcee-record-id') : null;
  }
  function bind() {
    for (var i = 0; i < NAVS.length; i++) {
      (function(nav){
        var nodes;
        try { nodes = document.querySelectorAll(nav.selector); } catch (err) { return; }
        for (var j = 0; j < nodes.length; j++) {
          (function(el){
            if (el.getAttribute('data-arcee-nav') === nav.id) return;
            el.setAttribute('data-arcee-nav', nav.id);
            var recordId = recordIdOf(el);
            var isRecord = RECORD.test(nav.href);
            if (el.tagName === 'A' && (!isRecord || recordId)) {
              el.setAttribute('href',
                isRecord ? nav.href.replace(new RegExp(RECORD.source, 'g'), encodeURIComponent(recordId)) : nav.href);
            }
            el.style.cursor = 'pointer';
          })(nodes[j]);
        }
      })(NAVS[i]);
    }
  }
  document.addEventListener('click', function(e){
    var target = e.target && e.target.nodeType === 1 ? e.target : (e.target && e.target.parentElement);
    if (!target || !target.closest) return;
    for (var i = 0; i < NAVS.length; i++) {
      var el;
      try { el = target.closest(NAVS[i].selector); } catch (err) { continue; }
      if (!el) continue;
      e.preventDefault();
      parent.postMessage({ type: '${NAV_MESSAGE_TYPE}', id: IFRAME_ID,
                           navId: NAVS[i].id, recordId: recordIdOf(el) }, '*');
      return;
    }
  });
  bind();
  if (document.readyState === 'complete') { setTimeout(bind, 0); } else { window.addEventListener('load', bind); }
  setTimeout(bind, 300);
  if (window.MutationObserver && document.body) {
    var queued = false;
    new MutationObserver(function(){
      if (queued) return;
      queued = true;
      setTimeout(function(){ queued = false; bind(); }, 0);
    }).observe(document.body, { childList: true, subtree: true });
  }
})();`;
}
